import { Array, Effect, Option, pipe } from "effect";
import { KvClientError, KvService } from "./types";
import { getTokenCmcMetaKey } from "./keys";
import { Schema } from "@effect/schema";
import { CmcTokenMeta } from "../cmc-client/types";
import { TokenUid } from "@silo/types";

export type GetUnpopulatedCmcTokens = ReturnType<
  typeof getUnpopulatedCmcTokens
>;
export type GetUnpopulatedCmcTokensSuccess =
  Effect.Effect.Success<GetUnpopulatedCmcTokens>;
export type GetUnpopulatedCmcTokensError =
  Effect.Effect.Error<GetUnpopulatedCmcTokens>;
export type GetUnpopulatedCmcTokensContext =
  Effect.Effect.Context<GetUnpopulatedCmcTokens>;

export function getUnpopulatedCmcTokens<T extends TokenUid>(tokens: T[]) {
  return Effect.gen(function* () {
    const kv = yield* KvService;

    const result = yield* Effect.tryPromise({
      try: async () => {
        const pipeline = kv.pipeline();

        for (const { chainKey, tokenAddress } of tokens) {
          pipeline.json.get(getTokenCmcMetaKey(chainKey, tokenAddress));
        }

        return pipeline.exec();
      },
      catch: (cause) => new KvClientError({ cause }),
    });

    const metaArray = yield* pipe(
      result,
      Schema.decodeUnknown(Schema.Array(Schema.NullOr(CmcTokenMeta)))
    );

    const unidentifiedTokens = tokens.filter((_, i) =>
      pipe(
        metaArray,
        Array.get(i),
        Option.flatMap(Option.fromNullable),
        Option.match({
          onNone: () => true,
          onSome: () => false,
        })
      )
    );

    return unidentifiedTokens;
  });
}
